import React, { useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import { Typography } from "@mui/material";
import Html2ReactParser from "html-to-react/lib/parser";

import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { ExpandButton } from "../Components/ExpandButton";
import { getType, types } from "./itemTypes";
import { useTranslation } from "react-i18next";
import StaticChoice from "./Choice/StaticChoice";
import Open from "./Open";
import StaticSlider from "./Slider/StaticSlider";

function QuestionContent({
  question,
  questionLeaf,
  handleQuestionTreeApi,
  isParent,
  isExpanded,
  position,
  disabled,
  provided,
  show,
  hideTree = false,
  onMouseDownHandler,
}) {
  const { t } = useTranslation();
  const htmlToReactParser = new Html2ReactParser();
  const reactElement = htmlToReactParser.parse(question.title);
  const formattedText = Boolean(
    question?.original?.itemExtra?.formattedText ?? 0
  );
  const mainQuestionType = getType(question.type);

  return (
    <Box display="flex" width="100%">
      {/** Label and expand button */}
      <Box
        width={92}
        display={"flex"}
        flexDirection={"column"}
        justifyContent={"space-between"}
      >
        {/** Label */}
        <Box>
          <Box
            {...provided.dragHandleProps}
            display="flex"
            gap={1}
            onMouseDown={() => onMouseDownHandler("drag")}
          >
            <DragIndicatorIcon
              fontSize="small"
              sx={{
                display: show && !disabled ? "flex" : "none",
                transform: "rotate(90deg)",
                color: "text.secondary",
              }}
            />
            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
              {position}
            </Typography>
          </Box>
          <Box
            onClick={(event) => {
              event.stopPropagation(); // prevent that click on card area was registered
              onMouseDownHandler("label");
            }}
            width={80}
          >
            {question.label === "" ? (
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                {t("editor.placeholders.label")}
              </Typography>
            ) : (
              <Typography noWrap variant="body2">
                {question.label}
              </Typography>
            )}
          </Box>
        </Box>
        {/** Expand button */}
        <Box marginLeft={-1} marginBottom={-1}>
          {!hideTree ? (
            <ExpandButton
              expand={isExpanded}
              onClick={() =>
                handleQuestionTreeApi({
                  type: "TOGGLE_EXPAND_QUESTION",
                  payload: {
                    key: questionLeaf.key,
                  },
                })
              }
              aria-expanded={isExpanded}
              aria-label="show conditional questions"
              size="small"
              fontSize="small"
              sx={{ display: isParent ? "flex" : "none" }}
            />
          ) : null}
        </Box>
      </Box>
      {/** Vertical divider */}
      <Box padding={1} />
      <Box sx={{ borderRight: "1px solid rgba(0, 0, 0, 0.2)" }} />
      <Box padding={1} />
      {/** Question */}
      <Box width="100%">
        <Box onClick={() => onMouseDownHandler("question")}>
          {question.title === "" ? (
            <Typography value="question" variant="body1" sx={{ opacity: 0.5 }}>
              {getType(question.type) !== types.unknown
                ? t(`editor.questionTypes.${question.type}.placeholder`)
                : `${question.type} ${t(`editor.placeholders.item`)}`}{" "}
            </Typography>
          ) : formattedText ? (
            <div value="question">{reactElement}</div>
          ) : (
            <Typography variant="body1" value="question">
              {question.title}
            </Typography>
          )}
        </Box>
        {mainQuestionType === types.choice ? (
          <StaticChoice
            type={question.type}
            options={question.options}
            onMouseDownHandler={onMouseDownHandler}
          />
        ) : mainQuestionType === types.slider ? (
          <StaticSlider
            question={question}
            onMouseDownHandler={onMouseDownHandler}
          />
        ) : (
          <Box marginLeft={-1}>
            <Open
              question={question}
              childrenLength={questionLeaf.children.length}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

function UnselectedQuestion(props) {
  const {
    indentLevel,
    questionLeaf,
    disabled = false,
    isExpanded,
    index,
    handleQuestionTreeApi,
    position,
  } = props;
  const indentMargin = (indentLevel.split(".").length - 2) * 4;
  const question = { ...questionLeaf.value, items: [] };
  const isParent = questionLeaf.children.length > 0;

  // An UI element is pressed
  const onMouseDownHandler = (type) => {
    const selectedId = `${questionLeaf.key}#${type ?? ""}`;
    handleQuestionTreeApi({
      type: "SELECT_QUESTION",
      payload: selectedId,
    });
  };

  // Hover
  const [show, setShow] = useState(false);
  const onMouseEnter = () => {
    setShow(true);
  };
  const onMouseLeave = () => {
    setShow(false);
  };

  return (
    <Draggable
      draggableId={question.id + ""}
      index={index}
      isDragDisabled={disabled}
    >
      {(provided) => (
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          id={question.id}
          paddingBottom={2}
          paddingLeft={indentMargin}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <Card
            sx={{
              display: "flex",
              background: "",
              borderBottom:
                isParent && !isExpanded
                  ? "7px solid rgba(0,0,0,0.12)"
                  : "1px solid rgba(0,0,0,0.12)",
              padding: 2,
            }}
            variant="outlined"
            onClick={() => onMouseDownHandler("question")}
          >
            <QuestionContent
              show={show}
              question={question}
              questionLeaf={questionLeaf}
              handleQuestionTreeApi={handleQuestionTreeApi}
              isParent={isParent}
              isExpanded={isExpanded}
              position={position}
              disabled={disabled}
              provided={provided}
              onMouseDownHandler={onMouseDownHandler}
            />
          </Card>
          <Box padding={1.5} />
        </Box>
      )}
    </Draggable>
  );
}

function questionsAreEqual(prevQuestions, nextQuestions) {
  return (
    prevQuestions.position === nextQuestions.position &&
    prevQuestions.isExpanded === nextQuestions.isExpanded &&
    JSON.stringify(prevQuestions.question) ===
      JSON.stringify(nextQuestions.question) &&
    prevQuestions.disabled === nextQuestions.disabled
  );
}

export default React.memo(UnselectedQuestion, questionsAreEqual);
